import React, { useCallback } from 'react';

import PropTypes from 'prop-types';
import { TextField } from '@picsio/ui';

import { useBlurCommit, useEnterToBlur, useLocalFieldValue } from '../../hooks';
import { serializeNumericValue } from '../utils';
import ProductFieldRow from './ProductFieldRow';
import ProductFieldLabel from './ProductFieldLabel';

const TextFieldField = ({
  data,
  value,
  disabled,
  onChange,
  placeholder,
  type,
  multiline,
  inputProps,
  fieldTooltip,
  isDiff,
  validate,
  savedError,
  commitAsNumber,
  onValidationChange,
}) => {
  const displayValue = value === null ? '' : value;
  const { localValue, setLocalValue } = useLocalFieldValue(displayValue);

  const handleCommit = useCallback((nextValue, onCancel) => {
    const trimmed = typeof nextValue === 'string' ? nextValue.trim() : nextValue;
    onChange(commitAsNumber ? serializeNumericValue(trimmed) : trimmed, data, onCancel);
  }, [commitAsNumber, data, onChange]);

  const { handleBlur, error: validationError, clearErrorIfValid } = useBlurCommit({
    value: displayValue,
    localValue,
    setLocalValue,
    onCommit: handleCommit,
    serialize: (nextValue) => String(nextValue ?? '').trim(),
    validate,
    savedError,
    onValidationChange,
  });

  const handleChange = useCallback((event) => {
    setLocalValue(event.target.value);
    clearErrorIfValid(event.target.value);
  }, [setLocalValue, clearErrorIfValid]);

  const handleKeyPress = useEnterToBlur();

  return (
    <ProductFieldRow title={data.title}>
      <TextField
        value={localValue}
        label={<ProductFieldLabel title={data.title} tooltip={fieldTooltip} isDiff={isDiff} />}
        disabled={disabled}
        placeholder={placeholder}
        type={type}
        error={Boolean(validationError)}
        helperText={validationError}
        InputProps={multiline ? {
          component: 'textarea',
          multiline: true,
          ...inputProps,
        } : inputProps}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyPress={handleKeyPress}
      />
    </ProductFieldRow>
  );
};

TextFieldField.propTypes = {
  data: PropTypes.shape({ title: PropTypes.string }).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  multiline: PropTypes.bool,
  inputProps: PropTypes.shape({}),
  fieldTooltip: PropTypes.string,
  isDiff: PropTypes.bool,
  validate: PropTypes.func,
  savedError: PropTypes.string,
  onValidationChange: PropTypes.func,
  commitAsNumber: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

TextFieldField.defaultProps = {
  value: '',
  disabled: false,
  placeholder: '',
  type: undefined,
  multiline: false,
  inputProps: undefined,
  fieldTooltip: '',
  isDiff: false,
  validate: null,
  savedError: null,
  onValidationChange: null,
  commitAsNumber: false,
};

export default TextFieldField;
